import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { catalogApi, Order } from '../services/catalogApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { WhatsappPhone } from '../store/services/whatsappApi';
import { format } from 'date-fns';
import MainLayout from '../components/layout/MainLayout';

const OrderManagement: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<number | null>(null);
  
  // Use RTK Query hooks
  const { data: whatsappPhones, isLoading: phonesLoading } = catalogApi.useGetWhatsappPhonesQuery();
  const { data: orders, isLoading: ordersLoading, refetch: refetchOrders } = 
    catalogApi.useGetOrdersQuery({ 
      whatsapp_phone_id: selectedPhoneId || undefined,
      status: selectedStatus !== 'all' ? selectedStatus : undefined
    });
  const { data: orderDetails } = 
    catalogApi.useGetOrderQuery(selectedOrder || 0, { skip: !selectedOrder });
  const [updateOrder] = catalogApi.useUpdateOrderMutation();
  const [initiatePayment] = catalogApi.useInitiatePaymentMutation();
  
  // Set first phone as default when data loads
  useEffect(() => {
    if (whatsappPhones && whatsappPhones.length > 0 && !selectedPhoneId) {
      setSelectedPhoneId(whatsappPhones[0].id);
    }
  }, [whatsappPhones, selectedPhoneId]);
  
  // Handle phone selection
  const handlePhoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPhoneId(parseInt(e.target.value));
  };
  
  // Handle status selection
  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
  };
  
  // Handle order selection
  const handleOrderClick = (orderId: number) => {
    setSelectedOrder(orderId);
  };
  
  // Handle order status update
  const handleStatusUpdate = async (orderId: number, newStatus: string) => {
    try {
      await updateOrder({
        id: orderId,
        status: newStatus
      });
      refetchOrders();
      
      if (selectedOrder === orderId) {
        setSelectedOrder(null);
        setTimeout(() => setSelectedOrder(orderId), 100);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };
  
  // Handle payment initiation
  const handleInitiatePayment = async (orderId: number) => {
    if (window.confirm('Are you sure you want to initiate payment for this order?')) {
      try {
        await initiatePayment(orderId);
        refetchOrders();
        
        if (selectedOrder === orderId) {
          setSelectedOrder(null);
          setTimeout(() => setSelectedOrder(orderId), 100);
        }
      } catch (error) {
        console.error('Error initiating payment:', error);
      }
    }
  };
  
  // Render status badge
  const renderStatusBadge = (status: string) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status) {
      case 'pending':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'paid':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'delivered':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${bgColor} ${textColor}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (e) {
      return dateString;
    }
  };
  
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700">
            You need to be logged in to manage orders.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <MainLayout>
      <div className="container mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Order Management</h1>
      </div>
      
      {/* Filters */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow">
        <div className="flex flex-col md:flex-row md:items-center md:space-x-4">
          <div className="mb-4 md:mb-0 md:w-1/3">
            <label htmlFor="phone-select" className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp Phone
            </label>
            <select
              id="phone-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              value={selectedPhoneId || ''}
              onChange={handlePhoneChange}
              disabled={phonesLoading}
            >
              <option value="">All Phones</option>
              {phonesLoading ? (
                <option>Loading phones...</option>
              ) : whatsappPhones && whatsappPhones.length > 0 ? (
                whatsappPhones.map((phone: WhatsappPhone) => (
                  <option key={phone.id} value={phone.id}>
                    {phone.display_name} ({phone.phone_number})
                  </option>
                ))
              ) : (
                <option>No WhatsApp phones available</option>
              )}
            </select>
          </div>
          
          <div className="md:w-1/3">
            <label htmlFor="status-select" className="block text-sm font-medium text-gray-700 mb-1">
              Order Status
            </label>
            <select
              id="status-select"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-whatsapp-green"
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Orders List */}
        <div className="lg:col-span-1 bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Orders
            </h3>
          </div>
          
          {ordersLoading ? (
            <LoadingSpinner />
          ) : orders && orders.length > 0 ? (
            <ul className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {orders.map((order: Order) => (
                <li 
                  key={order.id} 
                  className={`px-4 py-4 hover:bg-gray-50 cursor-pointer ${selectedOrder === order.id ? 'bg-blue-50' : ''}`}
                  onClick={() => handleOrderClick(order.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Order #{order.id}
                      </p>
                      <p className="text-sm text-gray-500">
                        {order.customer_name || order.customer_phone}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {order.total_price} {order.currency}
                      </p>
                    </div>
                    <div className="flex flex-col items-end">
                      {renderStatusBadge(order.status)}
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No orders found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try changing your filters or wait for new orders.
              </p>
            </div>
          )}
        </div>
        
        {/* Order Details */}
        <div className="lg:col-span-2 bg-white shadow sm:rounded-md">
          {selectedOrder && orderDetails ? (
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-xl font-semibold">Order #{orderDetails.id}</h2>
                  <p className="text-gray-500">
                    Created on {formatDate(orderDetails.created_at)}
                  </p>
                </div>
                <div className="flex space-x-2">
                  {orderDetails.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleInitiatePayment(orderDetails.id)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Request Payment
                      </button>
                      <button
                        onClick={() => handleStatusUpdate(orderDetails.id, 'cancelled')}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {orderDetails.status === 'paid' && (
                    <button
                      onClick={() => handleStatusUpdate(orderDetails.id, 'delivered')}
                      className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Mark Delivered
                    </button>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-lg mb-2">Customer Information</h3>
                  <p><span className="font-medium">Name:</span> {orderDetails.customer_name || 'N/A'}</p>
                  <p><span className="font-medium">Phone:</span> {orderDetails.customer_phone}</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-medium text-lg mb-2">Order Information</h3>
                  <p><span className="font-medium">Status:</span> {renderStatusBadge(orderDetails.status)}</p>
                  <p><span className="font-medium">Total:</span> {orderDetails.total_price} {orderDetails.currency}</p>
                  <p><span className="font-medium">Payment Method:</span> {orderDetails.payment_method}</p>
                  <p><span className="font-medium">Payment ID:</span> {orderDetails.payment_id || 'Not paid yet'}</p>
                </div>
              </div>
              
              <div className="mb-6">
                <h3 className="font-medium text-lg mb-2">Product</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center p-4">
                    {orderDetails.catalog_item.images && orderDetails.catalog_item.images.length > 0 ? (
                      <img 
                        src={orderDetails.catalog_item.images[0].original_url || '/placeholder-image.png'} 
                        alt={orderDetails.catalog_item.name}
                        className="h-16 w-16 object-cover rounded"
                      />
                    ) : (
                      <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                        <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">{orderDetails.catalog_item.name}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">{orderDetails.catalog_item.description}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {orderDetails.catalog_item.price} {orderDetails.catalog_item.currency} x {orderDetails.quantity}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Payment Transactions */}
              {orderDetails.payment_transactions && orderDetails.payment_transactions.length > 0 && (
                <div>
                  <h3 className="font-medium text-lg mb-2">Payment Transactions</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Transaction ID
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {orderDetails.payment_transactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(transaction.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {transaction.transaction_id || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {transaction.amount} {transaction.currency}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {renderStatusBadge(transaction.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No order selected</h3>
              <p className="mt-1 text-sm text-gray-500">
                Select an order from the list to view details.
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </MainLayout>
  );
};

export default OrderManagement;
